import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  Send, 
  ArrowLeft, 
  CheckCircle2, 
  Clock,
  Paperclip,
  X,
  Search,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const AdminSupportMessages = () => {
  const { toast } = useToast();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, open, resolved
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    loadConversations();
    
    // Poll for updates every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      loadConversations();
      if (selectedConversation) {
        loadConversationDetail(selectedConversation.user_id);
      }
    }, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const loadConversations = async () => {
    try {
      // Check both 'token' and 'adminToken' for compatibility
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
      if (!token) {
        console.error('No token found for admin');
        toast({
          title: "Authentication Required",
          description: "Please log in as admin",
          variant: "destructive"
        });
        return;
      }

      // Use relative URL - works on any domain
      console.log('Admin loading conversations from:', '/api/messages/admin/conversations');
      
      const response = await fetch('/api/messages/admin/conversations', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Admin conversations response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Admin conversations loaded:', data.length, 'conversations');
        setConversations(data);
      } else if (response.status === 403) {
        console.error('Admin access denied - insufficient permissions');
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: "Access Denied",
          description: errorData.detail || "Admin access required",
          variant: "destructive"
        });
      } else {
        console.error('Failed to load conversations:', response.status);
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: "Error",
          description: errorData.detail || "Failed to load conversations",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast({
        title: "Error",
        description: "Failed to load conversations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadConversationDetail = async (userId) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
      // Use relative URL - works on any domain
      const response = await fetch(`/api/messages/admin/conversation/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        setSelectedConversation({
          user_id: data.user_id,
          username: data.username,
          email: data.email,
          status: data.status
        });
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleSelectConversation = (conv) => {
    loadConversationDetail(conv.user_id);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB",
          variant: "destructive"
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;
    if (!selectedConversation) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
      // Use relative URL - works on any domain
      const formData = new FormData();
      formData.append('user_id', selectedConversation.user_id);
      formData.append('message_text', newMessage.trim() || '(File attachment)');
      if (selectedFile) {
        formData.append('attachment', selectedFile);
      }
      
      const response = await fetch('/api/messages/admin/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const sentMessage = await response.json();
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        toast({
          title: "Message sent",
          description: "User will be notified"
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsResolved = async () => {
    if (!selectedConversation) return;

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
      // Use relative URL - works on any domain
      const formData = new FormData();
      formData.append('status', 'resolved');
      
      const response = await fetch(
        `/api/messages/admin/conversation/${selectedConversation.user_id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );
      
      if (response.ok) {
        setSelectedConversation(prev => ({ ...prev, status: 'resolved' }));
        loadConversations();
        toast({
          title: "Conversation resolved",
          description: "Marked as resolved successfully"
        });
      }
    } catch (error) {
      console.error('Error marking as resolved:', error);
    }
  };

  const handleReopenConversation = async () => {
    if (!selectedConversation) return;

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
      // Use relative URL - works on any domain
      const formData = new FormData();
      formData.append('status', 'open');
      
      const response = await fetch(
        `/api/messages/admin/conversation/${selectedConversation.user_id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );
      
      if (response.ok) {
        setSelectedConversation(prev => ({ ...prev, status: 'open' }));
        loadConversations();
        toast({
          title: "Conversation reopened",
          description: "Marked as open successfully"
        });
      }
    } catch (error) {
      console.error('Error reopening conversation:', error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || conv.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unread_count, 0);

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900">
      {/* Sidebar - Conversations List */}
      <div className="w-80 bg-slate-800/50 backdrop-blur-sm border-r border-cyan-500/20 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-cyan-500/20">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-cyan-400 flex items-center gap-2">
              <MessageCircle className="w-6 h-6" />
              Support Messages
            </h2>
            <button
              onClick={loadConversations}
              className="p-2 hover:bg-cyan-500/10 rounded-lg text-cyan-400"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          
          {totalUnread > 0 && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-3 py-2 mb-4">
              <p className="text-red-400 text-sm font-semibold">
                {totalUnread} unread message{totalUnread !== 1 ? 's' : ''}
              </p>
            </div>
          )}
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-600" />
            <Input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-700/50 border-cyan-500/30 text-cyan-100"
            />
          </div>
          
          {/* Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filterStatus === 'all'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-slate-700/50 text-cyan-400 hover:bg-slate-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterStatus('open')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filterStatus === 'open'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-slate-700/50 text-cyan-400 hover:bg-slate-700'
              }`}
            >
              Open
            </button>
            <button
              onClick={() => setFilterStatus('resolved')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filterStatus === 'resolved'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-slate-700/50 text-cyan-400 hover:bg-slate-700'
              }`}
            >
              Resolved
            </button>
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-cyan-600">Loading...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-cyan-600">
              {searchTerm ? 'No conversations found' : 'No messages yet'}
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.user_id}
                onClick={() => handleSelectConversation(conv)}
                className={`p-4 border-b border-cyan-500/10 cursor-pointer transition ${
                  selectedConversation?.user_id === conv.user_id
                    ? 'bg-cyan-500/20 border-l-4 border-l-cyan-500'
                    : 'hover:bg-slate-700/30'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-cyan-300 truncate">
                      {conv.username}
                    </h3>
                    <p className="text-xs text-cyan-600 truncate">{conv.email}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {conv.unread_count > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                        {conv.unread_count}
                      </span>
                    )}
                    {conv.status === 'resolved' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    ) : (
                      <Clock className="w-4 h-4 text-yellow-400" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-cyan-500 truncate mb-1">
                  {conv.last_message}
                </p>
                <p className="text-xs text-cyan-700">
                  {formatTime(conv.last_message_time)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="bg-slate-800/50 backdrop-blur-sm border-b border-cyan-500/20 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-cyan-400">
                    {selectedConversation.username}
                  </h3>
                  <p className="text-sm text-cyan-600">{selectedConversation.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedConversation.status === 'resolved' ? (
                    <>
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Resolved
                      </span>
                      <Button
                        onClick={handleReopenConversation}
                        variant="outline"
                        size="sm"
                        className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                      >
                        Reopen
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Open
                      </span>
                      <Button
                        onClick={handleMarkAsResolved}
                        variant="outline"
                        size="sm"
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                      >
                        Mark Resolved
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.message_id}
                  className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${
                    msg.sender_type === 'admin'
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600'
                      : 'bg-slate-700/70 border border-cyan-500/20'
                  } rounded-2xl px-4 py-3`}>
                    <p className={`text-xs font-semibold mb-1 ${
                      msg.sender_type === 'admin' ? 'text-cyan-100' : 'text-cyan-400'
                    }`}>
                      {msg.sender_name}
                    </p>
                    <p className={`text-sm ${
                      msg.sender_type === 'admin' ? 'text-white' : 'text-cyan-300'
                    }`}>
                      {msg.message_text}
                    </p>
                    {msg.attachment_url && (
                      <a
                        href={'' + msg.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-sm mt-2 flex items-center gap-1 underline ${
                          msg.sender_type === 'admin' ? 'text-cyan-100' : 'text-cyan-400'
                        }`}
                      >
                        <Paperclip className="w-3 h-3" />
                        {msg.attachment_name}
                      </a>
                    )}
                    <p className={`text-xs mt-1 ${
                      msg.sender_type === 'admin' ? 'text-cyan-100' : 'text-cyan-700'
                    }`}>
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* File Preview */}
            {selectedFile && (
              <div className="bg-slate-800/50 border-t border-cyan-500/20 px-4 py-2">
                <div className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Paperclip className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="text-sm text-cyan-300 truncate">{selectedFile.name}</span>
                    <span className="text-xs text-cyan-600 flex-shrink-0">
                      ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="text-red-400 hover:text-red-300 ml-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="bg-slate-800/50 backdrop-blur-sm border-t border-cyan-500/20 p-4">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.zip"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  className="text-cyan-400 hover:bg-cyan-500/10"
                >
                  <Paperclip className="w-5 h-5" />
                </Button>
                <Input
                  type="text"
                  placeholder="Type your reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !sending && handleSendMessage()}
                  disabled={sending}
                  className="flex-1 bg-slate-700/50 border-cyan-500/30 text-cyan-100"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || (!newMessage.trim() && !selectedFile)}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
                >
                  {sending ? (
                    <Clock className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-cyan-600">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Select a conversation to start</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSupportMessages;
